import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import useAiQuota from '@/hooks/useAiQuota';
import useActivityCounters from '@/lib/useActivityCounters';
import {
  User, MapPin, Calendar, Stethoscope, BookOpen, ExternalLink,
  Plus, Trash2, Save, Loader2, CheckCircle, XCircle, Clock, AlertCircle, ToggleRight, Star, Sparkles,
  Camera, Upload, Wallet, UserX, Crown, CreditCard, Download, Lock, AlertTriangle, Copy, Share2, MessageCircle,
  FileText, Image, Send, SpellCheck, Bell, BellOff, BarChart3, TrendingUp, Eye, EyeOff, Phone, Search,
  Newspaper, History, Check, X, Edit3, RefreshCw, ChevronDown, QrCode, Zap, Gift, Ticket, ClipboardList,
  Network, UserPlus,
} from 'lucide-react';
import BookingChat from '@/components/BookingChat';
import PinSettings from '@/components/dashboard/PinSettings';
import ProfilePublishedCelebration from '@/components/dashboard/ProfilePublishedCelebration';
import MonogramAvatar from '@/components/MonogramAvatar';
import VoiceMeter from '@/components/dashboard/VoiceMeter';
import ClinicSearchPicker from '@/components/ClinicSearchPicker';
import ClinicMap from '@/components/ClinicMap';
import HospitalAttachmentsSection from '@/components/dashboard/HospitalAttachmentsSection';
import {
  Field, ShareUrlBar, PushNotificationToggle, RedeemCouponCard,
  VoiceAuthenticityNotice, ChangePasswordSection,
  WhatsNewLaunchBanner, SetupBlocker, WebsiteScoreWidget,
  TierBadgeWithUpgrade, UpgradeBanner, SubscriptionWarning,
} from '@/components/dashboard/widgets/SharedWidgets';

export default function LeadsTab() {
  const [leads, setLeads] = useState(null);
  const [loading, setLoading] = useState(true);

  // ── Connect-page leads (patient_directory rows) — the "On Patient's My
  //    Clinics Network" tile. Fetched in parallel with website-analytics so
  //    a doctor sees who tapped Connect alongside the macro funnel data.
  const [networkRows, setNetworkRows] = useState([]);
  const [networkLoading, setNetworkLoading] = useState(true);

  // Live Active-WakeUp counter — pulsing green dot + "new today" badge for
  // freshly-arrived Connect-page leads. Drives off the same poll that
  // powers the dashboard tab dot.
  const { statusFor, acknowledge } = useActivityCounters(true);
  const networkStatus = statusFor('leads');
  const ackedRef = useRef(false);

  useEffect(() => {
    api.get('/dashboard/leads')
      .then(r => setLeads(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));

    api.get('/dashboard/patients')
      .then(r => setNetworkRows(Array.isArray(r.data?.rows) ? r.data.rows : []))
      .catch(() => setNetworkRows([]))
      .finally(() => setNetworkLoading(false));
  }, []);

  // The doctor is looking at this tile — dismiss the pulsing dot (the
  // count badge sticks around until midnight per the wakeup spec).
  useEffect(() => {
    if (!loading && !networkLoading && !ackedRef.current) {
      ackedRef.current = true;
      acknowledge('leads');
    }
  }, [loading, networkLoading, acknowledge]);

  if (loading) return <div className="text-center py-12 text-slate-400 animate-pulse">Loading lead data...</div>;
  if (!leads) return <div className="text-center py-12 text-slate-500">Unable to load lead data.</div>;

  const cm = leads.current_month;
  const pm = leads.previous_month;
  const viewsTrend = pm.page_views > 0 ? Math.round(((cm.page_views - pm.page_views) / pm.page_views) * 100) : cm.page_views > 0 ? 100 : 0;
  const leadsTrend = pm.total_leads > 0 ? Math.round(((cm.total_leads - pm.total_leads) / pm.total_leads) * 100) : cm.total_leads > 0 ? 100 : 0;

  // Top sources sorted
  const sources = Object.entries(leads.source_breakdown || {}).sort((a, b) => b[1] - a[1]);
  const totalSrcEvents = sources.reduce((sum, [, count]) => sum + count, 0);

  const sourceLabels = { google: 'Google Search', direct: 'Direct URL', whatsapp: 'WhatsApp', facebook: 'Facebook', instagram: 'Instagram', twitter: 'Twitter/X', linkedin: 'LinkedIn', bing: 'Bing', yahoo: 'Yahoo', other: 'Other' };
  const sourceColors = { google: 'bg-sky-500', direct: 'bg-slate-500', whatsapp: 'bg-emerald-500', facebook: 'bg-blue-500', instagram: 'bg-pink-500', twitter: 'bg-slate-700', linkedin: 'bg-blue-700', bing: 'bg-teal-500', other: 'bg-slate-400' };

  return (
    <div className="space-y-6" data-testid="leads-tab">
      <div>
        <h2 className="text-xl font-medium text-slate-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Your Website Performance</h2>
        <p className="text-sm text-slate-500">{leads.month_label} — How patients are finding you</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Profile Views', value: cm.page_views, icon: Eye, color: 'sky', trend: viewsTrend },
          { label: 'WhatsApp Leads', value: cm.whatsapp_clicks, icon: MessageCircle, color: 'emerald' },
          { label: 'Phone Clicks', value: cm.phone_clicks, icon: Phone, color: 'amber' },
          { label: 'Bookings', value: cm.bookings, icon: Calendar, color: 'violet' },
        ].map(({ label, value, icon: Icon, color, trend }) => (
          <div key={label} className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={`lead-stat-${label.toLowerCase().replace(/ /g, '-')}`}>
            <div className="flex items-center justify-between mb-2">
              <Icon size={16} className={`text-${color}-500`} />
              {trend !== undefined && trend !== 0 && (
                <span className={`text-[10px] font-bold ${trend > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                  {trend > 0 ? '+' : ''}{trend}%
                </span>
              )}
            </div>
            <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{value}</p>
            <p className="text-xs text-slate-500 mt-0.5">{label}</p>
          </div>
        ))}
      </div>

      {/* Total Leads Summary */}
      <div className="bg-gradient-to-r from-sky-50 to-emerald-50 rounded-2xl border border-sky-100 p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-600">Total Qualified Leads This Month</p>
            <p className="text-3xl font-bold text-slate-900 mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{cm.total_leads}</p>
            <p className="text-xs text-slate-500 mt-1">WhatsApp messages + Phone calls + Bookings</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-slate-500">Last Month</p>
            <p className="text-lg font-bold text-slate-400">{pm.total_leads}</p>
            {leadsTrend !== 0 && (
              <p className={`text-xs font-bold ${leadsTrend > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                <TrendingUp size={12} className="inline mr-0.5" />{leadsTrend > 0 ? '+' : ''}{leadsTrend}%
              </p>
            )}
          </div>
        </div>
      </div>

      {/* On Patient's "My Clinics" Network — Connect-page leads tile with
          Active WakeUp (pulsing green dot + new-today badge). Shipped to
          surface Connect submissions inside My Leads alongside the macro
          funnel data so doctors see WHO walked in via /c/{slug}. */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="my-clinics-network-tile">
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-slate-100 bg-gradient-to-r from-emerald-50/60 to-sky-50/60">
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="relative inline-flex items-center justify-center w-9 h-9 rounded-xl bg-emerald-100 text-emerald-700 shrink-0">
              <Network size={16} />
              {networkStatus.hasUnseen && (
                <span aria-label="New leads today" className="absolute -top-0.5 -right-0.5 inline-flex h-2.5 w-2.5" data-testid="network-tile-dot">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
                </span>
              )}
            </span>
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-slate-900 truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>
                On Patient's <i>"My Trusted Medical Clinics"</i> Network
              </h3>
              <p className="text-[11px] text-slate-500 truncate">Patients who tapped <b>Connect</b> from your Doctor Connect page — your warmest verified opt-in leads.</p>
            </div>
          </div>
          {networkStatus.count > 0 && (
            <span
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 text-[11px] font-bold border border-emerald-300 shrink-0"
              title={`${networkStatus.count} new today`}
              data-testid="network-tile-count"
            >
              <Zap size={11} /> {networkStatus.count} new today
            </span>
          )}
        </div>

        {networkLoading ? (
          <div className="px-5 py-8 text-center text-xs text-slate-400 animate-pulse">Loading network leads…</div>
        ) : networkRows.length === 0 ? (
          <div className="px-5 py-8 text-center" data-testid="network-tile-empty">
            <UserPlus size={28} className="text-slate-300 mx-auto mb-2" />
            <p className="text-sm font-medium text-slate-700">No Connect-page leads yet</p>
            <p className="text-xs text-slate-500 mt-1 max-w-sm mx-auto">
              Share your Connect link with patients — every tap lands here with their name, mobile and clinic context.
            </p>
          </div>
        ) : (
          <>
            <ul className="divide-y divide-slate-100 max-h-80 overflow-y-auto" data-testid="network-tile-list">
              {networkRows.slice(0, 8).map((row) => {
                const last = row.last_seen_at ? new Date(row.last_seen_at) : null;
                const when = last ? last.toLocaleDateString(undefined, { day: '2-digit', month: 'short' }) : '—';
                const clinicLabel = row.last_location_name || row.location_name || row.location_city || 'Other';
                const visits = row.connect_count || 0;
                return (
                  <li key={row.id} className="flex items-center gap-3 px-5 py-3 hover:bg-slate-50" data-testid={`network-row-${row.id}`}>
                    <span className="w-9 h-9 rounded-full bg-sky-100 text-sky-700 flex items-center justify-center text-xs font-bold shrink-0">
                      {(row.name || '?').slice(0, 1).toUpperCase()}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-slate-900 truncate">{row.name || 'Unknown'}</p>
                      <p className="text-[11px] text-slate-500 truncate flex items-center gap-2">
                        <span className="inline-flex items-center gap-1"><Phone size={10} />{row.phone_masked || '—'}</span>
                        <span className="text-slate-300">·</span>
                        <span className="inline-flex items-center gap-1 truncate"><MapPin size={10} className="shrink-0" />{clinicLabel}</span>
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-[11px] text-slate-400">{when}</p>
                      {visits > 1 && (
                        <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-0.5 mt-0.5">
                          ×{visits}
                        </span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
            {networkRows.length > 8 && (
              <div className="px-5 py-3 text-center border-t border-slate-100">
                <Link to="/dashboard?tab=patient_log" className="text-xs font-semibold text-sky-700 hover:text-sky-900 inline-flex items-center gap-1" data-testid="network-tile-view-all">
                  View all {networkRows.length} in Patient Log <ExternalLink size={11} />
                </Link>
              </div>
            )}
          </>
        )}
      </div>

      {/* Lead Sources */}
      {sources.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h3 className="text-base font-medium text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Search size={16} className="inline mr-2 text-sky-600" />How Patients Find You
          </h3>
          <div className="space-y-3">
            {sources.map(([src, count]) => {
              const pct = totalSrcEvents > 0 ? Math.round((count / totalSrcEvents) * 100) : 0;
              return (
                <div key={src} data-testid={`source-${src}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-slate-700">{sourceLabels[src] || src}</span>
                    <span className="text-sm font-semibold text-slate-900">{pct}% <span className="text-slate-400 font-normal text-xs">({count})</span></span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${sourceColors[src] || 'bg-slate-400'}`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Empty state */}
      {cm.page_views === 0 && cm.total_leads === 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center">
          <Eye size={32} className="text-slate-300 mx-auto mb-3" />
          <h3 className="text-base font-medium text-slate-700" style={{ fontFamily: 'Outfit, sans-serif' }}>No Activity Yet</h3>
          <p className="text-sm text-slate-500 mt-2 max-w-sm mx-auto">
            Share your DocSite URL on WhatsApp, social media, and at your clinic. As patients visit, you'll see your lead data here.
          </p>
        </div>
      )}
    </div>
  );
}

